import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import { useEffect } from "react";
import {
  DEFAULT_LAYOUT_MODE,
  LAYOUT_STORAGE_KEY,
  resolveLayoutMode,
  type LayoutMode,
} from "@/lib/layout-mode";

/*
  Same shape as use-theme.ts, for the same reasons: a bare string rather than
  JSON so the boot script in index.html can read it without unquoting, and an
  unrecognised value falls back to the default rather than reaching the DOM.
*/
const layoutStorage = {
  getItem: (key: string, initialValue: LayoutMode): LayoutMode =>
    resolveLayoutMode(localStorage.getItem(key)) ?? initialValue,
  setItem: (key: string, value: LayoutMode) => localStorage.setItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(key),
};

export const layoutModeAtom = atomWithStorage<LayoutMode>(
  LAYOUT_STORAGE_KEY,
  DEFAULT_LAYOUT_MODE,
  layoutStorage,
  { getOnInit: true }
);

/** Reads and sets the layout preference ('split' | 'index'). */
export function useLayoutMode() {
  return useAtom(layoutModeAtom);
}

/**
 * Mirrors the preference onto `<html data-layout>`. Mount once, at the root (ThemeSync).
 * The boot script has already set it for first paint; this keeps it right after a change.
 */
export function useApplyLayoutMode() {
  const [mode] = useLayoutMode();

  useEffect(() => {
    document.documentElement.dataset.layout = mode;
  }, [mode]);
}
